import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import Head from '../../../layout/head/Head';
import Content from '../../../layout/content/Content';
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
} from '../../../components/Component';
import { tenant_auth_actions_helper } from '../../../actions/actions_helper';
import {
  fail_retrieve_tenant_dashboard_data,
  loading_retrieve_tenant_dashboard_data,
  reset_retrieve_tenant_dashboard_data,
  success_retrieve_tenant_dashboard_data,
} from '../../../reducers/auth_tenant/retrieve_tenant_dashboard_data_Slice';
import {
  RETRIEVE_TENANT_DASHBOARD_DATA_ERROR_MSG,
  RETRIEVE_TENANT_DASHBOARD_DATA_SUCCESS_MSG,
} from '../../../utils/error_success_messages';
import { RETRIEVE_TENANT_DASHBOARD_DATA_API } from '../../../api/api_data';
import { Link } from 'react-router-dom';
import AuthTenantPageError from '../../error/AuthTenantPageError';
import AuthTenantLoader from '../../loaders/AuthTenantLoader';
import AuthTenantNoData from '../../error/AuthTenantNoData';
import * as app_consts from '../../../utils/app_consts';
import { messageToast } from '../../../utils/Utils';
import TotalTenantSales from './tenant_dashboard_data/TotalTenantSales';
import StockTakeStatistics from './tenant_dashboard_data/StockTakeStatistics';
import RecentTenantOrders from './tenant_dashboard_data/RecentTenantOrders';
import TenantStoreStatistics from './tenant_dashboard_data/TenantStoreStatistics';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const TenantDashboard = ({ tenant_info }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page_data, setPageData] = useState([]);

  useEffect(() => {
    retrieve_dashboard_data();
  }, []);

  const retrieve_dashboard_data = () => {
    setError('');
    setLoading(true);
    dispatch(
      tenant_auth_actions_helper(
        loading_retrieve_tenant_dashboard_data,
        success_retrieve_tenant_dashboard_data,
        fail_retrieve_tenant_dashboard_data,
        RETRIEVE_TENANT_DASHBOARD_DATA_ERROR_MSG,
        RETRIEVE_TENANT_DASHBOARD_DATA_SUCCESS_MSG,
        app_consts.GET,
        `/${tenant_info?.url_prefix}/${RETRIEVE_TENANT_DASHBOARD_DATA_API}`
      )
    );
  };

  const retrieve_tenant_dashboard_data_listener = useSelector(
    (state) =>
      state.retrieve_tenant_dashboard_data.retrieve_tenant_dashboard_data
  );

  useEffect(() => {
    if (retrieve_tenant_dashboard_data_listener.status !== app_consts.IDLE)
    {
      if (retrieve_tenant_dashboard_data_listener.status === app_consts.LOADING)
      {
        setLoading(true);
        setError('');
      } else
      {
        if (retrieve_tenant_dashboard_data_listener.status === app_consts.FAIL)
        {
          setLoading(false);
          setError(retrieve_tenant_dashboard_data_listener.message);
          messageToast(
            retrieve_tenant_dashboard_data_listener.message,
            app_consts.FAIL
          );
        } else if (
          retrieve_tenant_dashboard_data_listener.status === app_consts.SUCCESS
        )
        {
          setPageData(retrieve_tenant_dashboard_data_listener.data);
          const timer = setTimeout(() => {
            setLoading(false);
          }, 600);
          return () => clearTimeout(timer);
        }
        dispatch(reset_retrieve_tenant_dashboard_data());
      }
    }
  }, [retrieve_tenant_dashboard_data_listener]);

  return (
    <>
      <Head
        title={
          tenant_info?.name ? `${tenant_info.name} Inventory Dashboard` : 'Inventory Dashboard'
        }
      />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Inventory Dashboard</BlockTitle>
              {!loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-2"
                >
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      retrieve_dashboard_data();
                    }}
                  >
                    <Button
                      color="light"
                      outline
                      className="bg-white d-none d-sm-inline-flex"
                    >
                      <span>Refresh List</span>
                    </Button>
                  </Link>
                </motion.div>
              )}
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthTenantLoader
                tenant_info={tenant_info}
                loading_message="Loading Dashboard Info..."
              />
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <AuthTenantPageError
                tenant_info={tenant_info}
                error_message={error}
              />
            </motion.div>
          ) : page_data && page_data !== null ? (
            <motion.div
              key="data"
              variants={containerVariants}
              initial="hidden"
              animate="show"
              exit="hidden"
            >
              <Block>
                <Row className="g-gs">
                  <motion.div variants={itemVariants} className="col-xxl-4 col-md-6">
                    <TotalTenantSales sales={page_data} />
                  </motion.div>

                  <motion.div variants={itemVariants} className="col-xxl-8 col-md-6">
                    <StockTakeStatistics sales={page_data} />
                  </motion.div>

                  <motion.div variants={itemVariants} className="col-xxl-8">
                    <RecentTenantOrders
                      sales={page_data?.five_recent_orders}
                      tenant_info={tenant_info}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="col-xxl-4 col-md-6">
                    <TenantStoreStatistics sales={page_data} />
                  </motion.div>
                </Row>
              </Block>
            </motion.div>
          ) : (
            <motion.div
              key="nodata"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthTenantNoData
                tenant_info={tenant_info}
                no_data_message={'No Data Found'}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Content>
    </>
  );
};

export default TenantDashboard;
